import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { ChevronRight, User, Calendar, MapPin } from 'lucide-react';

interface Application {
  id: number;
  reference_id: string;
  country_code: string;
  country_name: string;
  arrival_date: string;
  plan: string;
  application_status: string;
  created_at: string;
  order_id: number;
}

interface ApplicationsListProps {
  applications: Application[];
  maxItems?: number;
}

const ApplicationsList: React.FC<ApplicationsListProps> = ({ 
  applications, 
  maxItems = 10 
}) => {
  // Ensure applications is an array
  const applicationsArray = Array.isArray(applications) ? applications : [];
  const displayApplications = applicationsArray.slice(0, maxItems);

  const getStatusColor = (status: string) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    
    switch (status.toLowerCase()) {
      case 'approved':
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'processing':
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'unpaid':
        return 'bg-blue-100 text-blue-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    if (!status) return 'Bilinmiyor';
    
    switch (status.toLowerCase()) {
      case 'approved':
        return 'Onaylandı';
      case 'paid':
        return 'Ödendi';
      case 'processing':
        return 'İşlemde';
      case 'pending':
        return 'Beklemede';
      case 'unpaid':
        return 'Ödenmedi';
      case 'rejected':
        return 'Reddedildi';
      default:
        return status;
    }
  };

  if (displayApplications.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Henüz başvuru bulunmuyor.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {displayApplications.map((application) => (
        <Link
          key={application.id}
          to={`/applications/${application.id}`}
          className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {application.reference_id}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {application.plan}
                  </p>
                </div>
              </div>
              
              <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                <div className="flex items-center">
                  <MapPin className="h-3 w-3 mr-1" />
                  {application.country_name}
                </div>
                <div className="flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  {format(new Date(application.created_at), 'dd MMM yyyy', { locale: tr })}
                </div>
              </div>
              
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">
                  #{application.order_id || 'N/A'}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(application.application_status)}`}>
                  {getStatusText(application.application_status)}
                </span>
              </div>
            </div>
            
            <div className="flex-shrink-0 ml-4">
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </Link>
      ))}
      
      {applications.length > maxItems && (
        <div className="text-center pt-4">
          <Link
            to="/applications"
            className="text-sm text-indigo-600 hover:text-indigo-500 font-medium"
          >
            Tümünü görüntüle ({applications.length})
          </Link>
        </div>
      )}
    </div>
  );
};

export default ApplicationsList;

